(() => {
  'use strict';
  if (window.FastAsleepCart) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const money = (value, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: currency || window.Shopify?.currency?.active || 'USD'
  }).format(Number(value) / 100);
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const text = (tag, value, className) => {
    const node = document.createElement(tag);
    node.textContent = value;
    if (className) node.className = className;
    return node;
  };
  const safeURL = (value, sameOrigin = false) => {
    try {
      const url = new URL(value, location.origin);
      return ['http:', 'https:'].includes(url.protocol) && (!sameOrigin || url.origin === location.origin) ? url.href : '';
    } catch { return ''; }
  };
  const focusables = (container) => [...container.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')]
    .filter(node => !node.disabled && !node.closest('[hidden], [inert]') && node.getClientRects().length);

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root() + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description :
          typeof data.message === 'string' ? data.message : 'Shopify could not update this cart.');
        error.confirmedRejection = true;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }

  // Reads share the write queue so a late /cart.js response cannot overwrite a newer mutation.
  let queue = Promise.resolve();
  let pending = 0;
  let uncertain = false;
  const enqueue = (work) => {
    pending += 1;
    emit('fam:cart-busy', { busy: true });
    const task = queue.then(work);
    queue = task.catch(() => {});
    return task.finally(() => {
      pending -= 1;
      emit('fam:cart-busy', { busy: pending > 0 });
    });
  };
  async function readCart() {
    const cart = await request('cart.js');
    uncertain = false;
    emit('fam:cart-updated', { cart });
    return cart;
  }
  async function mutate(path, payload) {
    if (uncertain) throw new Error('Refresh your cart before making another change. The previous request could not be confirmed.');
    let mutationError;
    try { await request(path, payload); } catch (error) { mutationError = error; }
    // Even a rejected multi-item add can change quantities. Always reconcile, never retry a POST.
    try { await readCart(); } catch {
      uncertain = true;
      const error = new Error('Your cart could not be confirmed. Refresh your cart before adding again.');
      emit('fam:cart-uncertain', { message: error.message });
      throw error;
    }
    if (mutationError) {
      throw mutationError.confirmedRejection ? mutationError :
        new Error('The request was interrupted. Your cart has been refreshed; check its contents before trying again.');
    }
  }
  const Cart = Object.freeze({
    add: (items) => enqueue(async () => {
      await mutate('cart/add.js', { items });
      emit('fam:items-added', { items: items.map(item => ({ id: item.id })) });
    }),
    change: (key, quantity) => enqueue(() => mutate('cart/change.js', { id: key, quantity })),
    refresh: () => enqueue(readCart),
    open: (trigger = document.activeElement, { refresh = true } = {}) => {
      const drawer = document.querySelector('fam-cart');
      if (drawer) drawer.open(trigger, refresh);
      else location.assign(root() + 'cart');
    }
  });
  window.FastAsleepCart = Cart;
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-fam-cart-open]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    Cart.open(trigger);
  });
  document.addEventListener('fam:cart-updated', ({ detail: { cart } }) => {
    document.querySelectorAll('[data-fam-cart-count]').forEach(node => { node.textContent = cart.item_count; });
  });

  class FamCart extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-fam-cart-status]');
      this.error = this.querySelector('[data-fam-cart-error]');
      this.items = this.querySelector('[data-fam-items]');
      this.addEventListener('click', event => this.onClick(event), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const nodes = focusables(this.dialog);
        const first = nodes[0];
        const last = nodes.at(-1);
        if (!first) { event.preventDefault(); this.dialog.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        document.querySelectorAll('[data-fam-cart-open]').forEach(node => node.setAttribute('aria-expanded', 'false'));
        const target = this.returnFocus?.isConnected ? this.returnFocus : document.querySelector('[data-fam-cart-open]');
        target?.focus({ preventScroll: true });
      }, options);
      document.addEventListener('fam:cart-updated', ({ detail: { cart } }) => this.render(cart), options);
      document.addEventListener('fam:cart-busy', ({ detail: { busy } }) => this.setBusy(busy), options);
      document.addEventListener('fam:cart-uncertain', ({ detail }) => this.showError(detail.message), options);
      document.addEventListener('shopify:section:select', event => {
        if (event.target.contains(this)) this.open(document.activeElement);
      }, options);
    }
    disconnectedCallback() {
      if (this.dialog.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow || '';
      }
      this.events?.abort();
    }
    async open(trigger, refresh = true) {
      if (!this.dialog.open) {
        this.returnFocus = trigger;
        this.previousOverflow = document.documentElement.style.overflow;
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        document.querySelectorAll('[data-fam-cart-open]').forEach(node => node.setAttribute('aria-expanded', 'true'));
      }
      this.clearError();
      if (!refresh && this.cart) return;
      try { await Cart.refresh(); } catch { this.showError('Unable to load your cart. Please refresh your cart.'); }
    }
    close() { this.dialog.close(); }
    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.querySelector('[data-fam-cart-retry]').hidden = true;
    }
    showError(message) {
      this.status.textContent = '';
      this.error.textContent = message;
      this.error.hidden = false;
      this.querySelector('[data-fam-cart-retry]').hidden = false;
    }
    setBusy(busy) {
      this.busy = busy;
      this.items.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-fam-change], [data-fam-upsell-add], [data-fam-checkout], [data-fam-cart-retry]')
        .forEach(node => { node.disabled = busy || (node.hasAttribute('data-fam-checkout') && uncertain); });
      if (!busy && this.focusAfter) {
        const target = focusables(this.dialog).find(node => node.dataset.famFocus === this.focusAfter);
        (target || this.querySelector('[data-fam-close]')).focus({ preventScroll: true });
        this.focusAfter = null;
      }
    }
    async onClick(event) {
      if (event.target.closest('[data-fam-close]')) { this.close(); return; }
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        return;
      }
      if (event.target.closest('[data-fam-cart-retry]')) { await this.open(this.returnFocus); return; }
      const change = event.target.closest('[data-fam-change]');
      const upsell = event.target.closest('[data-fam-upsell-add]');
      if ((!change && !upsell) || this.busy) return;
      this.clearError();
      this.focusAfter = (change || upsell).dataset.famFocus;
      try {
        if (change) await Cart.change(change.dataset.key, Number(change.dataset.quantity));
        else await Cart.add([{ id: Number(upsell.dataset.famUpsellAdd), quantity: 1 }]);
      } catch (error) { this.showError(error.message); }
    }
    render(cart) {
      this.cart = cart;
      this.clearError();
      const currency = cart.currency;
      const fragment = document.createDocumentFragment();
      cart.items.forEach(item => {
        const row = text('li', '', 'fam-line');
        if (item.image && safeURL(item.image)) {
          const image = document.createElement('img');
          const url = new URL(safeURL(item.image));
          url.searchParams.set('width', '160');
          image.src = url.href; image.alt = item.featured_image?.alt || item.product_title;
          image.width = 80; image.height = 80; image.loading = 'lazy'; image.fetchPriority = 'auto';
          image.className = 'fam-thumb'; row.append(image);
        } else row.append(text('div', '', 'fam-thumb fam-thumb--empty'));
        const content = text('div', '', 'fam-line__content');
        const link = text('a', item.product_title);
        link.href = safeURL(item.url, true) || root() + 'cart';
        content.append(link);
        if (item.variant_title && item.variant_title !== 'Default Title') content.append(text('p', item.variant_title, 'fam-small'));
        if (item.selling_plan_allocation) content.append(text('p', item.selling_plan_allocation.selling_plan.name, 'fam-small'));
        Object.entries(item.properties || {}).forEach(([key, value]) => {
          if (!key.startsWith('_') && value !== '' && value !== null) content.append(text('p', `${key}: ${value}`, 'fam-small'));
        });
        const price = text('p', '', 'fam-line__price');
        if (item.original_line_price > item.final_line_price) {
          const original = text('s', money(item.original_line_price, currency));
          original.setAttribute('aria-label', `Original price ${money(item.original_line_price, currency)}`);
          price.append(original, document.createTextNode(' '));
        }
        price.append(text('strong', money(item.final_line_price, currency)));
        content.append(price);
        (item.line_level_discount_allocations || []).forEach(discount => content.append(text('p', discount.discount_application.title, 'fam-small')));
        const controls = text('div', '', 'fam-quantity');
        const button = (label, quantity, value, suffix) => {
          const node = text('button', value, 'fam-icon-button');
          node.type = 'button'; node.setAttribute('aria-label', `${label} ${item.product_title}${item.variant_title ? ', ' + item.variant_title : ''}`);
          node.dataset.famChange = ''; node.dataset.key = item.key; node.dataset.quantity = quantity;
          node.dataset.famFocus = `${item.key}:${suffix}`;
          return node;
        };
        controls.append(button('Decrease quantity of', Math.max(0, item.quantity - 1), '\u2212', 'minus'));
        const quantity = text('span', item.quantity);
        quantity.setAttribute('aria-label', `Quantity ${item.quantity}`);
        controls.append(quantity, button('Increase quantity of', item.quantity + 1, '+', 'plus'));
        const remove = button('Remove', 0, 'Remove', 'remove');
        remove.className = 'fam-remove'; controls.append(remove);
        content.append(controls); row.append(content); fragment.append(row);
      });
      this.items.replaceChildren(fragment);
      this.querySelector('[data-fam-empty]').hidden = cart.item_count !== 0;
      this.querySelector('[data-fam-cart-footer]').hidden = cart.item_count === 0;
      this.querySelector('[data-fam-total]').textContent = money(cart.total_price, currency);
      const discounts = this.querySelector('[data-fam-cart-discounts]');
      discounts.replaceChildren(...(cart.cart_level_discount_applications || []).map(discount =>
        text('li', `${discount.title}: -${money(discount.total_allocated_amount, currency)}`)));
      this.status.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in your cart.`;
      const threshold = Number(this.dataset.threshold);
      const shipping = this.querySelector('[data-fam-shipping]');
      shipping.hidden = this.dataset.shippingEnabled !== 'true' || threshold <= 0 || !Number.isFinite(threshold) ||
        currency !== this.dataset.currency || !cart.items.some(item => item.requires_shipping);
      if (!shipping.hidden) {
        const remaining = Math.max(0, threshold - cart.total_price);
        shipping.querySelector('progress').value = Math.max(0, Math.min(100, cart.total_price / threshold * 100));
        shipping.querySelector('[data-fam-shipping-message]').textContent = remaining > 0 ?
          `${money(remaining, currency)} away from free shipping.` : 'Free shipping threshold reached.';
      }
      this.querySelectorAll('[data-fam-upsell]').forEach(node => {
        node.hidden = cart.items.some(item => String(item.product_id) === node.dataset.famUpsell);
        node.querySelector('[data-fam-upsell-add]').dataset.famFocus = `upsell:${node.dataset.famUpsell}`;
      });
      this.setBusy(this.busy);
    }
  }

  class FamProduct extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.error = this.querySelector('[data-fam-product-error]');
      this.status = this.querySelector('[data-fam-product-status]');
      this.addEventListener('change', event => {
        if (event.target.matches('[data-fam-option]')) this.loadOptions(event.target);
        else if (event.target.matches('[data-fam-purchase], [data-fam-plan]')) this.syncPurchase();
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-fam-variant-retry]')) this.loadOptions();
      }, options);
      this.addEventListener('submit', event => this.submit(event), options);
      this.setup();
    }
    disconnectedCallback() {
      this.events?.abort(); this.variantRequest?.abort(); this.observer?.disconnect();
    }
    setup(previous) {
      this.form = this.querySelector('[data-fam-product-form]');
      this.observer?.disconnect();
      if (!this.form) { this.querySelector('[data-fam-sticky]')?.setAttribute('hidden', ''); return; }
      this.form.querySelectorAll('[data-fam-option], [data-fam-purchase]').forEach(node => { node.disabled = false; });
      const plan = this.form.querySelector('[data-fam-plan]');
      if (previous) {
        const radio = this.form.querySelector(`[data-fam-purchase][value="${previous.purchase}"]`);
        if (radio) radio.checked = true;
        if (plan && [...plan.options].some(option => option.value === previous.plan)) plan.value = previous.plan;
        const quantity = this.form.elements.quantity;
        const min = Number(quantity.min) || 1;
        const step = Number(quantity.step) || 1;
        const nextQuantity = min + Math.max(0, Math.ceil((previous.quantity - min) / step)) * step;
        quantity.value = quantity.max ? Math.min(nextQuantity, Number(quantity.max)) : nextQuantity;
      }
      this.syncPurchase(false);
      const primary = this.form.querySelector('[data-fam-primary-atc]');
      const sticky = this.querySelector('[data-fam-sticky]');
      if (primary && sticky && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          sticky.hidden = !show;
          sticky.inert = !show;
        }, { threshold: 0 });
        this.observer.observe(primary);
      }
    }
    syncPurchase(updateURL = true) {
      if (!this.form) return;
      this.form.querySelectorAll('[data-fam-purchase]').forEach(node => { node.disabled = !!this.loading || !!this.submitting; });
      this.form.elements.quantity.disabled = !!this.loading || !!this.submitting;
      const plan = this.form.querySelector('[data-fam-plan]');
      const subscription = !!plan && (this.form.dataset.requiredPlan === 'true' ||
        this.form.querySelector('[data-fam-purchase]:checked')?.value === 'subscription');
      const selected = plan?.selectedOptions[0];
      if (plan) {
        plan.disabled = !subscription || !!this.loading || !!this.submitting;
        this.form.querySelector('[data-fam-plan-fields]').hidden = !subscription;
        const compare = Number(selected?.dataset.comparePrice || this.form.dataset.price);
        const price = Number(selected?.dataset.price);
        const saving = compare > 0 ? Math.round((compare - price) / compare * 100) : 0;
        this.form.querySelector('[data-fam-subscribe-label]').textContent = saving > 0 ? `Subscribe & Save ${saving}%` : 'Subscribe';
        this.form.querySelector('[data-fam-plan-price]').textContent =
          `${money(price, this.form.dataset.currency)} per order; ${money(selected?.dataset.perDelivery || price, this.form.dataset.currency)} per delivery. See the selected plan for future charges.`;
      }
      const price = subscription ? Number(selected?.dataset.price) : Number(this.form.dataset.price);
      this.querySelectorAll('[data-fam-price], [data-fam-sticky-price]').forEach(node => {
        node.textContent = this.form.elements.id.value ? money(price, this.form.dataset.currency) : 'Unavailable combination';
      });
      const canBuy = this.form.dataset.available === 'true' && !!this.form.elements.id.value &&
        (!subscription || !!selected?.value) && !this.loading && !this.submitting && !this.failedOptions;
      const primary = this.form.querySelector('[data-fam-primary-atc]');
      const unavailable = !this.form.elements.id.value ? 'Unavailable combination' :
        this.form.dataset.available !== 'true' ? 'Sold out or unavailable' : 'Add to cart';
      [primary, this.querySelector('[data-fam-sticky-atc]')].filter(Boolean).forEach(button => {
        button.disabled = !canBuy;
        button.textContent = this.submitting ? 'Adding...' : this.loading ? 'Updating...' : this.failedOptions ? 'Update options to continue' :
          canBuy ? this.form.dataset.buttonLabel : unavailable;
      });
      if (updateURL && this.dataset.updateUrl === 'true' && this.form.elements.id.value && !this.loading && !this.failedOptions) {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.form.elements.id.value);
        url.searchParams.delete('option_values');
        if (subscription) url.searchParams.set('selling_plan', selected.value);
        else url.searchParams.delete('selling_plan');
        history.replaceState(history.state, '', url);
      }
    }
    async loadOptions(changedOption) {
      if (!this.form || this.submitting) return;
      this.variantRequest?.abort();
      const controller = new AbortController();
      this.variantRequest = controller;
      const previous = {
        purchase: this.form.querySelector('[data-fam-purchase]:checked')?.value || 'once',
        plan: this.form.querySelector('[data-fam-plan]')?.value,
        quantity: Number(this.form.elements.quantity.value) || 1
      };
      const url = new URL(this.dataset.productUrl, location.origin);
      const destination = changedOption?.selectedOptions[0]?.dataset.productUrl;
      if (destination && safeURL(destination, true) && new URL(destination, location.origin).pathname !== url.pathname) {
        location.assign(destination); return;
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('option_values', [...this.form.querySelectorAll('[data-fam-option]')].map(node => node.value).join(','));
      const view = new URL(location.href).searchParams.get('view');
      if (view) url.searchParams.set('view', view);
      this.loading = true;
      this.error.hidden = true;
      this.querySelector('[data-fam-variant-retry]').hidden = true;
      this.querySelector('[data-fam-product-recovery]').hidden = true;
      this.status.textContent = 'Updating your options...';
      this.syncPurchase(false);
      const timeout = setTimeout(() => controller.abort('timeout'), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Variant response failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextProduct = [...html.querySelectorAll('fam-product')].find(node => node.dataset.sectionId === this.dataset.sectionId);
        const next = nextProduct?.querySelector('[data-fam-product-content]');
        if (!next?.querySelector('[data-fam-product-form]')) throw new Error('Variant section missing');
        if (this.variantRequest !== controller || !this.isConnected) return;
        const focusedPosition = this.contains(document.activeElement) ? document.activeElement.dataset.position : null;
        this.querySelector('[data-fam-product-content]').replaceWith(document.importNode(next, true));
        this.loading = false; this.failedOptions = false;
        this.setup(previous);
        this.syncPurchase();
        this.status.textContent = this.form.dataset.available !== 'true' ? 'This option is sold out or unavailable.' :
          previous.purchase === 'subscription' && !this.form.querySelector('[data-fam-plan]') ?
            'Subscriptions are unavailable for this option. One-time purchase selected.' : 'Options updated.';
        if (focusedPosition) this.querySelector(`[data-position="${focusedPosition}"]`)?.focus({ preventScroll: true });
      } catch {
        if (this.variantRequest !== controller || !this.isConnected) return;
        this.loading = false; this.failedOptions = true;
        this.status.textContent = '';
        this.error.textContent = 'Unable to update these options. Retry before adding this product.';
        this.error.hidden = false;
        this.querySelector('[data-fam-variant-retry]').hidden = false;
        this.syncPurchase(false);
      } finally { clearTimeout(timeout); }
    }
    async submit(event) {
      if (!event.target.matches('[data-fam-product-form]')) return;
      event.preventDefault();
      if (this.loading || this.submitting || this.failedOptions || this.form.dataset.available !== 'true' || !this.form.reportValidity()) return;
      const trigger = event.submitter || document.activeElement;
      const formData = new FormData(this.form);
      const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity')) };
      if (formData.get('selling_plan')) item.selling_plan = Number(formData.get('selling_plan'));
      if (!item.id || !Number.isInteger(item.quantity) || item.quantity < 1) return;
      this.submitting = true;
      this.error.hidden = true;
      this.querySelector('[data-fam-product-recovery]').hidden = true;
      this.form.querySelectorAll('[data-fam-option], [data-fam-purchase]').forEach(node => { node.disabled = true; });
      this.syncPurchase(false);
      try {
        await Cart.add([item]);
        this.status.textContent = 'Added to your cart.';
        Cart.open(trigger, { refresh: false });
      } catch (error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
        this.querySelector('[data-fam-product-recovery]').hidden = false;
      } finally {
        this.submitting = false;
        if (this.isConnected) {
          this.form.querySelectorAll('[data-fam-option], [data-fam-purchase]').forEach(node => { node.disabled = false; });
          this.syncPurchase(false);
        }
      }
    }
  }

  const questions = {
    environment: {
      title: 'Does light or sound make it harder to sleep?',
      answers: [['yes', 'Yes, light or sound bothers me'], ['no', 'No, my room feels comfortable']],
      next: () => 'breathing'
    },
    breathing: {
      title: 'Does nasal stuffiness or snoring interrupt your night?',
      answers: [['yes', 'Yes, sometimes or often'], ['no', 'No']],
      next: answers => answers.breathing === 'yes' ? 'breathing_detail' : answers.environment === 'yes' ? 'environment_detail' : 'mind_detail'
    },
    breathing_detail: {
      title: 'Have you or someone else noticed gasping or pauses in your breathing?',
      answers: [['yes', 'Yes'], ['unsure', 'I am not sure'], ['no', 'No, mainly nasal stuffiness']],
      next: () => 'result'
    },
    environment_detail: {
      title: 'Which would you most like to change?',
      answers: [['light', 'Light reaching my eyes'], ['sound', 'Noise around me'], ['both', 'Both light and sound']],
      next: () => 'result'
    },
    mind_detail: {
      title: 'What makes winding down difficult?',
      answers: [['racing', 'A busy or racing mind'], ['routine', 'An inconsistent bedtime routine'], ['unsure', 'I am still figuring it out']],
      next: () => 'result'
    }
  };
  class FamQuiz extends HTMLElement {
    connectedCallback() {
      this.events?.abort(); this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.form = this.querySelector('[data-fam-quiz-form]');
      this.result = this.querySelector('[data-fam-results]');
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-fam-answer]')) return;
        this.answers[this.state] = event.target.value;
        this.querySelector('[data-fam-next]').disabled = false;
      }, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.answers[this.state]) return;
        const next = questions[this.state].next(this.answers);
        if (next === 'result') this.showResult();
        else { this.history.push(this.state); this.state = next; this.renderQuestion(true); }
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-fam-back]')) {
          delete this.answers[this.state];
          this.state = this.history.pop() || 'environment';
          this.renderQuestion(true);
        }
        if (event.target.closest('[data-fam-restart]') && !this.busy) this.restart(true);
        if (event.target.closest('[data-fam-add-bundle]')) this.addBundle(event.target);
      }, options);
      this.restart();
    }
    disconnectedCallback() { this.events?.abort(); }
    restart(focus = false) {
      this.answers = {}; this.history = []; this.state = 'environment'; this.bundleItems = [];
      this.querySelector('[data-fam-quiz-error]').hidden = true;
      this.querySelector('[data-fam-quiz-status]').textContent = '';
      this.querySelector('[data-fam-quiz-recovery]').hidden = true;
      this.renderQuestion(focus);
    }
    renderQuestion(focus) {
      this.form.hidden = false; this.result.hidden = true;
      const question = questions[this.state];
      const heading = this.querySelector('[data-fam-question]');
      heading.textContent = question.title;
      this.querySelector('[data-fam-quiz-step]').textContent = `Question ${this.history.length + 1} of 3`;
      this.querySelector('progress').value = this.history.length + 1;
      const answers = this.querySelector('[data-fam-answers]');
      answers.replaceChildren(...question.answers.map(([value, label]) => {
        const wrapper = text('label', '', 'fam-radio');
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `fam-answer-${this.dataset.sectionId}`; input.value = value;
        input.dataset.famAnswer = ''; input.required = true; input.checked = this.answers[this.state] === value;
        wrapper.append(input, text('span', label)); return wrapper;
      }));
      this.querySelector('[data-fam-back]').hidden = this.history.length === 0;
      this.querySelector('[data-fam-next]').disabled = !this.answers[this.state];
      this.querySelector('[data-fam-next]').textContent = this.history.length === 2 ? 'See my result' : 'Continue';
      if (focus) heading.focus({ preventScroll: true });
    }
    showResult() {
      this.form.hidden = true; this.result.hidden = false;
      const guidanceOnly = this.answers.breathing === 'yes' && this.answers.breathing_detail !== 'no';
      const branch = this.answers.breathing === 'yes' ? 'airway' : this.answers.environment === 'yes' ? 'sensory' : 'mind';
      const copy = {
        sensory: ['A calmer sleep space', this.answers.environment_detail === 'light' ? 'Start with the light around you. Review these sleep-space essentials.' :
          this.answers.environment_detail === 'sound' ? 'Start with the noise around you. Review these sleep-space essentials.' : 'Bring light and sound into your bedtime routine.'],
        airway: ['Breathing comfort', 'Explore comfort products alongside advice from your clinician. These products do not treat breathing disorders.'],
        mind: ['Make time to wind down', this.answers.mind_detail === 'racing' ? 'Create a repeatable transition from a busy day to bedtime.' : 'Start with a consistent wind-down routine.']
      };
      const heading = this.querySelector('[data-fam-result-heading]');
      heading.textContent = guidanceOnly ? 'Start with a conversation about your breathing' : copy[branch][0];
      this.querySelector('[data-fam-result-copy]').textContent = guidanceOnly ? 'A product bundle cannot answer this concern.' : copy[branch][1];
      this.querySelector('[data-fam-guidance]').hidden = branch !== 'airway';
      this.querySelectorAll('[data-fam-bundle]').forEach(node => { node.hidden = guidanceOnly || node.dataset.famBundle !== branch; });
      const items = [...this.querySelector(`[data-fam-bundle="${branch}"]`).querySelectorAll('[data-fam-bundle-item]')];
      const available = !guidanceOnly && items.length > 0 && items.every(item => item.dataset.available === 'true' && Number(item.dataset.id) > 0);
      const merged = new Map();
      if (available) items.forEach(item => {
        const id = Number(item.dataset.id);
        merged.set(id, (merged.get(id) || 0) + Number(item.dataset.quantity));
      });
      this.bundleItems = [...merged].map(([id, quantity]) => ({ id, quantity }));
      this.querySelector('[data-fam-bundle-unavailable]').hidden = guidanceOnly || available;
      const button = this.querySelector('[data-fam-add-bundle]');
      button.hidden = guidanceOnly; button.disabled = !available; button.textContent = 'Add bundle to cart';
      heading.focus({ preventScroll: true });
    }
    async addBundle(trigger) {
      if (this.busy || !this.bundleItems.length) return;
      this.busy = true; trigger.disabled = true; trigger.textContent = 'Adding bundle...';
      const error = this.querySelector('[data-fam-quiz-error]');
      error.hidden = true;
      this.querySelector('[data-fam-restart]').disabled = true;
      try {
        await Cart.add(this.bundleItems);
        this.querySelector('[data-fam-quiz-status]').textContent = 'Your bundle was added.';
        Cart.open(trigger, { refresh: false });
      } catch (failure) {
        error.textContent = failure.message; error.hidden = false;
        this.querySelector('[data-fam-quiz-recovery]').hidden = false;
      } finally {
        this.busy = false;
        trigger.disabled = false; trigger.textContent = 'Add bundle to cart';
        this.querySelector('[data-fam-restart]').disabled = false;
      }
    }
  }

  customElements.define('fam-cart', FamCart);
  customElements.define('fam-product', FamProduct);
  customElements.define('fam-quiz', FamQuiz);
})();
